import { faPen, faTrash } from "@fortawesome/free-solid-svg-icons"
import Select from "react-select"
import Button from "components/Shared/Button/Button"
import HeaderFilters from "components/Shared/HeaderFilters/HeaderFilters"
import { GET_ITEM_LIMIT } from "constants/constants"
import { Checkbox, Modal, Pagination } from "@wfp/ui"
import BaseTable, { AutoResizer, Column, ColumnShape } from "react-base-table"
import { SubmitHandler } from "react-hook-form"
import { useState } from "react"
import { GorpViewData } from "interfaces/dataInterfaces"
import {
  useGetGorpsQuery,
  useUpdateGorpMutation,
  useDeleteGorpMutation,
  useGetYearsQuery,
  useGetCountriesQuery,
} from "services/gorp"
import EditData, { regions, visibility } from "./EditData"

export default function ViewData() {
  const [pageNumber, setPageNumber] = useState(1)
  const [getItemLimit, setGetItemLimit] = useState(GET_ITEM_LIMIT)
  const [year, setYear] = useState(null)
  const [month, setMonth] = useState(null)
  const [countryCode, setCountryCode] = useState("")
  const [regionCode, setRegionCode] = useState("")
  const [actionItem, setActionItem] = useState<{
    item: GorpViewData
    action: "edit" | "delete"
  } | null>(null)

  const { data: years } = useGetYearsQuery()
  const { data: countries } = useGetCountriesQuery()
  const [updateGorp] = useUpdateGorpMutation()
  const [deleteGorp] = useDeleteGorpMutation()

  const onSubmit: SubmitHandler<any> = async data => {
    await updateGorp(data)
    setActionItem(null)
  }

  const { data: gorps } = useGetGorpsQuery({
    year,
    month,
    countryCode,
    regionCode,
    skip: pageNumber === 1 ? 0 : (pageNumber - 1) * getItemLimit,
    top: getItemLimit,
  })

  const [showGropModal, setShowGorpModal] = useState(false)

  const handleDelete = async (id: number) => {
    await deleteGorp(id)
    setActionItem(null)
  }

  const handleNewEditModalClose = () => {
    actionItem ? setActionItem(null) : setShowGorpModal(false)
  }

  const countryData = [{ value: "", label: "All Countries" }]
  countries?.map(c => {
    countryData.push({
      value: c.value,
      label: c.name,
    })
  })

  const yearData = [{ value: 0, label: "All years" }]
  years?.map(c => {
    yearData.push({
      value: Number(c.value),
      label: c.name,
    })
  })

  const monthData = [
    { value: 0, label: "All months" },
    { value: 1, label: "Jan" },
    { value: 2, label: "Feb" },
    { value: 3, label: "Mar" },
    { value: 4, label: "Apr" },
    { value: 5, label: "May" },
    { value: 6, label: "Jun" },
    { value: 7, label: "Jul" },
    { value: 8, label: "Aug" },
    { value: 9, label: "Sep" },
    { value: 10, label: "Oct" },
    { value: 11, label: "Nov" },
    { value: 12, label: "Dec" },
  ]

  const getCountrydata = async (event: any) => {
    setCountryCode(event.value)
  }

  const getRegiondata = async (event: any) => {
    setRegionCode(event.value)
  }

  const getYeardata = async (event: any) => {
    setYear(event.value)
  }

  const getMonthdata = async (event: any) => {
    setMonth(event.value)
  }

  const parseGorpValue = (value?: number, inMain: boolean = false) => {
    if (inMain === true) {
      return "In Main"
    }
    if (value === undefined) {
      return "Null"
    }
    return value
  }

  const parsePrevalence = (prevalence?: number, inMain: boolean = false) => {
    if (inMain === true) {
      return ""
    }
    if (prevalence === undefined) {
      return "Null"
    }
    return prevalence
  }

  const parseVisibility = (visibilityId?: number) => {
    if (visibilityId === undefined) {
      return visibilityId
    }
    const visibilityObject = visibility.find(v => v.value === visibilityId)
    if (visibilityObject === undefined) {
      return visibilityId
    }
    return visibilityObject.label
  }

  const centeredHeaderRenderer = ({ column }) => (
    <div className="BaseTable__header-cell-text text-center">{column.title}</div>
  )

  const columns: Array<ColumnShape<GorpViewData>> = [
    {
      key: "gorpID",
      dataKey: "gorpID",
      title: "ID",
      width: 70,
      align: Column.Alignment.RIGHT,
      headerRenderer: centeredHeaderRenderer,
    },
    {
      key: "gorpDateYear",
      dataKey: "gorpDateYear",
      title: "Year",
      width: 55,
      align: Column.Alignment.CENTER,
      headerRenderer: centeredHeaderRenderer,
    },
    {
      key: "gorpDateMonth",
      dataKey: "gorpDateMonth",
      title: "Month",
      width: 65,
      align: Column.Alignment.CENTER,
      headerRenderer: centeredHeaderRenderer,
    },
    {
      key: "gorpVisibilityId",
      dataKey: "gorpVisibilityId",
      title: "Visibility",
      width: 80,
      align: Column.Alignment.CENTER,
      headerRenderer: centeredHeaderRenderer,
      dataGetter: ({ rowData }) => parseVisibility(rowData.gorpVisibilityId),
    },
    {
      key: "gorpRegionName",
      dataKey: "gorpRegionName",
      title: "Region",
      width: 70,
      align: Column.Alignment.CENTER,
      headerRenderer: centeredHeaderRenderer,
    },
    {
      key: "countryOfficeIso3",
      dataKey: "countryOfficeIso3",
      title: "Country",
      width: 70,
      align: Column.Alignment.CENTER,
      headerRenderer: centeredHeaderRenderer,
    },
    {
      key: "gorpTotalValue",
      dataKey: "gorpTotalValue",
      title: "Total Value",
      width: 90,
      align: Column.Alignment.RIGHT,
      headerRenderer: centeredHeaderRenderer,
      dataGetter: ({ rowData }) => parseGorpValue(rowData.gorpTotalValue),
    },
    {
      key: "gorpTotalPrevalence",
      dataKey: "gorpTotalPrevalence",
      title: "Total Prev.",
      width: 90,
      align: Column.Alignment.RIGHT,
      headerRenderer: centeredHeaderRenderer,
      dataGetter: ({ rowData }) => parsePrevalence(rowData.gorpTotalPrevalence),
    },
    {
      key: "gorpMainValue",
      dataKey: "gorpMainValue",
      title: "Main Value",
      width: 90,
      align: Column.Alignment.RIGHT,
      headerRenderer: centeredHeaderRenderer,
      dataGetter: ({ rowData }) => parseGorpValue(rowData.gorpMainValue),
    },
    {
      key: "gorpMainPrevalence",
      dataKey: "gorpMainPrevalence",
      title: "Main Prev.",
      width: 90,
      align: Column.Alignment.RIGHT,
      headerRenderer: centeredHeaderRenderer,
      dataGetter: ({ rowData }) => parsePrevalence(rowData.gorpMainPrevalence),
    },
    {
      key: "gorpAdditionalRefugeesValue",
      dataKey: "gorpAdditionalRefugeesValue",
      title: "Refugees Value",
      width: 90,
      align: Column.Alignment.RIGHT,
      headerRenderer: centeredHeaderRenderer,
      dataGetter: ({ rowData }) => parseGorpValue(rowData.gorpAdditionalRefugeesValue, rowData.areRefugeesInMain),
    },
    {
      key: "gorpAdditionalRefugeesPrevalence",
      dataKey: "gorpAdditionalRefugeesPrevalence",
      title: "Refugees Prev.",
      width: 90,
      align: Column.Alignment.RIGHT,
      headerRenderer: centeredHeaderRenderer,
      dataGetter: ({ rowData }) => parsePrevalence(rowData.gorpAdditionalRefugeesPrevalence, rowData.areRefugeesInMain),
    },
    {
      key: "gorpAdditionalIdpvalue",
      dataKey: "gorpAdditionalIdpvalue",
      title: "IDP Value",
      width: 90,
      align: Column.Alignment.RIGHT,
      headerRenderer: centeredHeaderRenderer,
      dataGetter: ({ rowData }) => parseGorpValue(rowData.gorpAdditionalIdpvalue, rowData.areIdpinMain),
    },
    {
      key: "gorpAdditionalIdpprevalence",
      dataKey: "gorpAdditionalIdpprevalence",
      title: "IDP Prev.",
      width: 90,
      align: Column.Alignment.RIGHT,
      headerRenderer: centeredHeaderRenderer,
      dataGetter: ({ rowData }) => parsePrevalence(rowData.gorpAdditionalIdpprevalence, rowData.areIdpinMain),
    },
    {
      key: "gorpIsBaseline",
      title: "Base",
      width: 50,
      align: Column.Alignment.CENTER,
      headerRenderer: centeredHeaderRenderer,
      cellRenderer: ({ rowData }) => (
        <Checkbox
          checked={rowData.gorpIsBaseline}
          id="gorpCheckbox"
          labelText=""
          name="checkbox-1"
          value="standard"
          readOnly
        />
      ),
    },
    {
      key: "action",
      title: "Action",
      width: 220,
      align: Column.Alignment.CENTER,
      headerRenderer: centeredHeaderRenderer,
      cellRenderer: ({ rowData }) => (
        <>
          <Button
            action={() =>
              setActionItem({
                item: rowData,
                action: "edit",
              })
            }
            label="Edit"
            className="btn-full blue action-btn"
            icon={faPen}
          />

          <Button
            action={() =>
              setActionItem({
                item: rowData,
                action: "delete",
              })
            }
            label="Delete"
            className="btn-full danger action-btn"
            style={{ marginLeft: "10px" }}
            icon={faTrash}
          />
        </>
      ),
    },
  ]

  return (
    <>
      <HeaderFilters>
        <div className="d-flex" style={{ width: "80%" }}>
          <Select
            isSearchable
            className="dropdown mr-4"
            classNamePrefix="select"
            placeholder="Select Year"
            options={yearData}
            onChange={value => getYeardata(value)}
          />
          <Select
            isSearchable
            className="dropdown mr-4"
            classNamePrefix="select"
            placeholder="Select Month"
            options={monthData}
            onChange={value => getMonthdata(value)}
          />
          <Select
            isSearchable
            className="dropdown mr-4"
            classNamePrefix="select"
            placeholder="Select Country"
            options={countryData}
            onChange={value => getCountrydata(value)}
          />
          <Select
            isSearchable
            className="dropdown"
            classNamePrefix="select"
            placeholder="Select Region"
            options={[{ value: undefined, label: "All regions" }].concat(regions as any)}
            onChange={value => getRegiondata(value)}
          />
        </div>
      </HeaderFilters>
      <div style={{ marginBottom: "20px" }} />

      <div style={{ height: "100%", minHeight: "60vh" }}>
        <AutoResizer>
          {({ width, height }) => (
            <BaseTable
              fixed
              data={gorps?.items}
              width={width}
              height={height}
              rowKey="gorpID"
              className="custom-table"
              rowHeight={38}
              headerHeight={45}
              columns={columns}
              emptyRenderer={() => <p>No records to display</p>}
            />
          )}
        </AutoResizer>
      </div>
      <Modal
        open={actionItem?.action === "delete"}
        modalHeading="Confirm Delete"
        primaryButtonText="Delete"
        secondaryButtonText="Cancel"
        onRequestSubmit={() => handleDelete(actionItem!.item.gorpID)}
        onSecondarySubmit={() => setActionItem(null)}
        onRequestClose={() => setActionItem(null)}
      >
        Are you sure you want to delete the selected record?
      </Modal>

      {(showGropModal || actionItem?.action === "edit") && (
        <EditData onSubmit={onSubmit} handleClose={handleNewEditModalClose} actionItem={actionItem?.item} />
      )}

      <Pagination
        totalItems={gorps?.count ?? 0}
        pageSize={getItemLimit}
        pageSizes={[10, 20, 30]}
        page={pageNumber}
        onChange={(event: any): void => {
          setPageNumber((event as any).page)
          setGetItemLimit((event as any).pageSize)
        }}
      />
    </>
  )
}
